import { Select } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import type { AdminSkill } from '@prepzio/shared';
import { useMemo, useState } from 'react';

import { useSkills } from '../hooks/use-content';
import { useT } from '../i18n';

/**
 * PRE-155: one searchable skill picker, asking the server.
 *
 * Every picker in the panel used to be a `Select` handed the whole skills list.
 * That worked while there were 52 rows and stopped working at 2,482: the list
 * arrived as 386 KB on every screen, and a dropdown of two and a half thousand
 * options is a scroll, not a choice.
 *
 * So the search moved to the server (`GET /v1/admin/skills?q=`), and this is the
 * one component that talks to it. Two details are what make it usable rather
 * than merely correct:
 *
 *   - **The selected row is always an option.** A picker whose options come from
 *     a search will, the moment the search changes, stop containing what is
 *     already selected — and Mantine renders a `value` with no matching option
 *     as an empty box. `selected` is merged in so an existing choice survives
 *     typing, and a form does not silently look like nothing was chosen.
 *   - **Debounced, and the previous page stays put while the next loads**
 *     (`placeholderData` in `useSkills`), so the list does not blank on every
 *     keystroke.
 */
export interface SkillPickerProps {
  label?: string;
  description?: string;
  placeholder?: string;
  value: string | null;
  onChange: (id: string | null) => void;
  /**
   * The chosen row, not just its id. A caller that names the selection in its
   * own copy ("X will redirect to Y") cannot look it up in a list it no longer
   * holds, which is the trade for moving search to the server.
   */
  onChangeSkill?: (skill: AdminSkill | null) => void;
  /** Rows to hide — a merge target that is itself queued, a skill's own id. */
  excludeIds?: readonly string[];
  /** The currently-selected skill, if the caller already has it. */
  selected?: AdminSkill | null;
  origin?: AdminSkill['origin'];
  clearable?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: string | null;
}

/** How many rows the dropdown shows before asking the person to narrow it. */
const PAGE = 50;

export function SkillPicker({
  label,
  description,
  placeholder,
  value,
  onChange,
  onChangeSkill,
  excludeIds,
  selected = null,
  origin,
  clearable,
  disabled,
  required,
  error,
}: SkillPickerProps) {
  const t = useT();
  const [search, setSearch] = useState('');
  const [debounced] = useDebouncedValue(search, 250);
  const query = useSkills({
    ...(debounced.trim() ? { q: debounced.trim() } : {}),
    ...(origin ? { origin } : {}),
    limit: PAGE,
  });

  const exclude = useMemo(() => new Set(excludeIds ?? []), [excludeIds]);
  const total = query.data?.total ?? 0;

  const data = useMemo(() => {
    const rows = (query.data?.items ?? []).filter((s) => !exclude.has(s.id));
    const options = rows.map((s) => ({ value: s.id, label: `${s.title} (${s.slug})` }));
    // Keep the current choice selectable even when the search no longer returns
    // it — otherwise picking A, then searching for B, blanks the field.
    if (selected && !options.some((o) => o.value === selected.id)) {
      options.unshift({ value: selected.id, label: `${selected.title} (${selected.slug})` });
    }
    return options;
  }, [query.data, exclude, selected]);

  // Say when there is more than what is shown. A dropdown that silently stops at
  // fifty of two thousand looks like the catalog ends there.
  const truncated = total > data.length;
  const hint =
    truncated && debounced.trim()
      ? t('skillPicker.hintFiltered', { shown: data.length, total })
      : truncated
        ? t('skillPicker.hintAll', { shown: data.length, total })
        : null;

  return (
    <Select
      label={label}
      description={description ?? hint ?? undefined}
      placeholder={placeholder ?? t('skillPicker.placeholder')}
      searchable
      clearable={clearable}
      disabled={disabled}
      required={required}
      error={error ?? undefined}
      nothingFoundMessage={t(query.isPending ? 'skillPicker.searching' : 'skillPicker.noMatch')}
      value={value}
      data={data}
      searchValue={search}
      onSearchChange={setSearch}
      onChange={(id) => {
        onChange(id);
        onChangeSkill?.(
          id == null ? null : ((query.data?.items ?? []).find((s) => s.id === id) ?? selected),
        );
      }}
    />
  );
}
